import React, { Children, useEffect, useState } from 'react';
import { CreateFormFeatureDialog } from 'components';
import { WALLETS_AND_GROUPS_PATHS } from 'constants/wallets-and-groups-paths';
import {
    SYSTEM_WALLETS,
    walletsAndGroupsCommonStyles,
    walletsAndGroupsConfig,
} from 'pages/wallets-and-groups/wallets-and-groups-helper';
import { useNavigate } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import InvalidPermissionsDialog from '../../../../components/invalid-permissions-dialog/InvalidPermissionsDialog';
import {
    createAndUpdateSystemWalletDefaults,
    CreateAndUpdateSystemWalletFormData,
    CreateAndUpdateSystemWalletSchema,
    createSystemWalletStyles,
    LabelComponent,
    tooltipMessages,
    typeData,
} from './create-or-edit-system-wallet-helper';
import { useSystemWallet, useUserGroupList } from 'hooks';
import { useAuth, usePermission } from '../../../../core';
import { Grid, MenuItem } from '@mui/material';
import { Select } from '@biq/apex-ui';
import { AutocompleteUserGroupField } from './AutocompleteUserGroupField';
import { DescriptionTextField } from '../description-field/DescriptionTextField';

export const CreateSystemWallet = (): React.JSX.Element => {
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isWriteUserGroupFieldEnabled, setIsWriteUserGroupFieldEnabled] =
        useState<boolean>(false);
    const { getToken } = useAuth();
    const { mutate: uploadSystemWallet } = useSystemWallet();
    const navigate = useNavigate();
    const { data: userGroupsData, isLoading: userGroupsLoading } =
        useUserGroupList();
    const { hasCurrentPermissionAndRole } = usePermission();
    const [displayPermissionDialog, setDisplayPermissionDialog] =
        useState(false);
    const [optionsData, setOptionsData] = useState<any[]>([]);

    const {
        handleSubmit,
        control,
        watch,
        setValue,
        formState: { errors },
    } = useForm<CreateAndUpdateSystemWalletFormData>({
        mode: 'all',
        resolver: yupResolver(CreateAndUpdateSystemWalletSchema),
        defaultValues: createAndUpdateSystemWalletDefaults,
    });

    useEffect(() => {
        if (!userGroupsLoading && userGroupsData) {
            let userGroupsDataArr = [];
            userGroupsDataArr = userGroupsData?.data.map((userGroup) => ({
                name: userGroup.name,
                id: userGroup.id,
            }));
            userGroupsDataArr.sort((a: any, b: any) => {
                return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
            });
            setOptionsData(userGroupsDataArr);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userGroupsLoading]);

    useEffect(() => {
        if (
            watch('type') === 'TENANT_OWNER' ||
            watch('type') === 'TENANT_LIMITED_OWNER'
        ) {
            setIsWriteUserGroupFieldEnabled(true);
        } else {
            setIsWriteUserGroupFieldEnabled(false);
            setValue('writeUserGroup', {
                name: '',
                id: '',
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [watch('type')]);

    const createSystemWallet = (
        data: CreateAndUpdateSystemWalletFormData
    ): void => {
        setIsLoading(true);
        uploadSystemWallet(
            {
                token: getToken(),
                description: data.description,
                type: data.type,
                writeUserGroup: data.writeUserGroup.id,
                accessControlUserGroup: data.accessControlUserGroup.id,
            },
            {
                onSuccess: () => {
                    setIsLoading(false);
                    setTimeout(() => {
                        navigate(
                            `${WALLETS_AND_GROUPS_PATHS.WALLETS_AND_GROUPS_PATH}?option=${SYSTEM_WALLETS}`
                        );
                    }, 500);
                },
                onError: () => {
                    setIsLoading(false);
                },
            }
        );
    };

    const onSubmit = (): void => {
        const permission = hasCurrentPermissionAndRole(
            'createMetadata',
            'sdzGovernance'
        );
        if (permission) {
            handleSubmit(createSystemWallet)();
        } else {
            setDisplayPermissionDialog(true);
        }
    };

    const handlePermissionDialogClose = (): void => {
        setDisplayPermissionDialog(false);
    };

    const handleCloseButton = (): void => {
        navigate(
            `${WALLETS_AND_GROUPS_PATHS.WALLETS_AND_GROUPS_PATH}?option=${SYSTEM_WALLETS}`
        );
    };

    const handleBackButton = (): void => {
        navigate(
            `${WALLETS_AND_GROUPS_PATHS.CREATE_NEW_WALLETS_AND_GROUPS_PATH}?option=${SYSTEM_WALLETS}`
        );
    };

    return (
        <CreateFormFeatureDialog
            buttonText='Save'
            handleSubmit={handleSubmit(onSubmit)}
            isLoading={isLoading}
            handleCloseButton={handleCloseButton}
            handleBackButton={handleBackButton}
            listConfig={walletsAndGroupsConfig}
        >
            <Grid sx={createSystemWalletStyles.mainContainer}>
                <Grid sx={walletsAndGroupsCommonStyles.gridStyles}>
                    <LabelComponent
                        label={'Type'}
                        tooltipMessage={tooltipMessages('type')}
                    />
                    <Controller
                        name='type'
                        control={control}
                        render={({ field }): React.JSX.Element => (
                            <Select
                                {...field}
                                variant='outlined'
                                error={!!errors.type}
                                errorText={
                                    errors.type ? errors.type.message : ''
                                }
                            >
                                {Children.toArray(
                                    typeData.map((type) => (
                                        <MenuItem value={type.value}>
                                            {type.name}
                                        </MenuItem>
                                    ))
                                )}
                            </Select>
                        )}
                    />
                </Grid>
                <Grid sx={walletsAndGroupsCommonStyles.gridStyles}>
                    <LabelComponent
                        label={'Access Control User Group'}
                        tooltipMessage={tooltipMessages(
                            'accessControlUserGroup'
                        )}
                    />
                    <AutocompleteUserGroupField
                        name='accessControlUserGroup'
                        control={control}
                        error={errors.accessControlUserGroup}
                        fieldValue={watch('accessControlUserGroup')}
                        options={optionsData}
                        onChange={(value): void =>
                            setValue('accessControlUserGroup', value)
                        }
                    />
                </Grid>
                {isWriteUserGroupFieldEnabled ? (
                    <Grid sx={walletsAndGroupsCommonStyles.gridStyles}>
                        <LabelComponent
                            label={'Write User Group'}
                            tooltipMessage={tooltipMessages('writeUserGroup')}
                        />
                        <AutocompleteUserGroupField
                            name='writeUserGroup'
                            control={control}
                            error={errors.writeUserGroup}
                            fieldValue={watch('writeUserGroup')}
                            options={optionsData}
                            onChange={(value): void =>
                                setValue('writeUserGroup', value)
                            }
                        />
                    </Grid>
                ) : null}
                <DescriptionTextField
                    control={control}
                    rows={2}
                    error={errors.description}
                    required={true}
                />
            </Grid>
            {displayPermissionDialog && (
                <InvalidPermissionsDialog
                    open={displayPermissionDialog}
                    onClose={handlePermissionDialogClose}
                />
            )}
        </CreateFormFeatureDialog>
    );
};
